import Group from "../../groups/Group";
import { LearningAnswerSpeech } from "../../learning/model/LearningAnswerSpeech";
import { LearningAnswerType } from "../../learning/model/LearningAnswerType";
import { LearningReviewSortStyle } from "../../learning/model/LearningReviewSortStyle";
import { LearningTimeLimit } from "../../learning/model/LearningTimeLimit";
import { LearningType } from "../../learning/model/LearningType";
import { LearningHistoryRange } from "../../learning/model/learningHistoryRange";
import { TermLevel } from "../../term/custom_view/TermLevel";
import { MemoPosition } from "../model/MemoPosition";
import { PronunciationPosition } from "../model/PronunciationPosition";
import { QuestionType } from "../model/QuestionType";
import { SortType } from "../model/SortType";

const learningSelectedGroupsKey = "LearningSelectedGroupsKey";
const learningSelectedLevlesKey = "LearningSelectedLevelsKey";
const learningSortTypeKey = "LearningSortTypeKey";
const learningQuestionTypeKey = "LearningQuestionTypeKey";
const learningMemoPositionKey = "LearningMemoPositionKey";
const flashcardsQuestionTypeKey = "FlashcardsQuestionTypeKey";
const multipleChoiceQuestionTypeKey = "MultipleChoiceQuestionTypeKey";
const flashcardsMemoPositionKey = "FlashcardsMemoPositionKey";
const multipleChoiceMemoPositionKey = "MultipleChoiceMemoPositionKey";
const spellingQuizMemoPositionKey = "SpellingQuizMemoPositionKey";
const learningPronunciationPrositionKey = "LearningPronunciationPositionKey";
const learningSectionBasedStudyKey = "LearningSectionBasedStudy";
const learningTextToSpeechKey = "LearningTextToSpeechKey";
const multipleChoiceWrongAnswerNoteKey = "MultipleChoiceWrongAnswerNoteKey";
const spellingQuizWrongAnswerNoteKey = "SpellingQuizWrongAnswerNoteKey";
const learningIntervalUnitCountKey = "LearningIntervalUnitCountKey";
const spellingQuizQuestionType = "SpellingQuizQuestionTypeKey";
const learningReviewLatestRangeKey = "LearningReviewLatestRangeKey";
const learningReviewLatestTypeKey = "LearningReviewLatestTypeKey";
const learningReviewLatestAnswerTypeKey = "LearningReviewLatestAnswerType";
const learningShowEnglishPronunciationSelectViewKey =
  "LearningShowEnglishPronunciationSelectViewKey";
const learningReviewGroupSortStyleKey = "LearningReviewGroupSortStyle";

const KEY_MULTIPLE_CHOICE_TIME_LIMIT_KEY = "MultipleChoiceTimeLimit";
const KEY_LEARNING_ANSWER_SPEECH = "LearningAnswerSpeech";

export function initializeLearningSettings() {
  localStorage.removeItem(learningSelectedGroupsKey);
  localStorage.removeItem(learningSelectedLevlesKey);
  localStorage.removeItem(learningSortTypeKey);
  localStorage.removeItem(learningQuestionTypeKey);
  localStorage.removeItem(flashcardsQuestionTypeKey);
  localStorage.removeItem(multipleChoiceQuestionTypeKey);
  localStorage.removeItem(learningPronunciationPrositionKey);
  localStorage.removeItem(learningTextToSpeechKey);
  localStorage.removeItem(multipleChoiceWrongAnswerNoteKey);
  localStorage.removeItem(spellingQuizWrongAnswerNoteKey);
  localStorage.removeItem(learningSectionBasedStudyKey);
  localStorage.removeItem(spellingQuizQuestionType);
  localStorage.removeItem(learningReviewGroupSortStyleKey);
  localStorage.removeItem(KEY_MULTIPLE_CHOICE_TIME_LIMIT_KEY);
  localStorage.removeItem(KEY_LEARNING_ANSWER_SPEECH);
}

export function getLearningAnswerSpeechFromLocalStroage(): LearningAnswerSpeech {
  const stored = localStorage.getItem(KEY_LEARNING_ANSWER_SPEECH);
  if (stored === null || stored === undefined) {
    return LearningAnswerSpeech.onlyIncorrect;
  }
  return JSON.parse(stored);
}

export function setLearningAnswerSpeechToLocalStorage(
  option: LearningAnswerSpeech
) {
  if (option === null || option === undefined) return;
  localStorage.setItem(KEY_LEARNING_ANSWER_SPEECH, JSON.stringify(option));
}

export function getMultipleChoiceTimeLimitFromLocalStroage(): LearningTimeLimit {
  const stored = localStorage.getItem(KEY_MULTIPLE_CHOICE_TIME_LIMIT_KEY);
  if (stored === null || stored === undefined) {
    return LearningTimeLimit.slower;
  }
  return JSON.parse(stored);
}

export function setMultipleChoiceTimeLimitToLocalStorage(
  limit: LearningTimeLimit
) {
  if (limit === null || limit === undefined) return;
  localStorage.setItem(
    KEY_MULTIPLE_CHOICE_TIME_LIMIT_KEY,
    JSON.stringify(limit)
  );
}

export function isOnShowingEnglishPronunciationSelectViewFromLocalStroage(): boolean {
  const value = localStorage.getItem(
    learningShowEnglishPronunciationSelectViewKey
  );
  if (value === null) {
    setIsOnShowingEnglishPronunciationSelectViewToLocalStorage(true);
    return true;
  }

  return value === "on";
}

export function setIsOnShowingEnglishPronunciationSelectViewToLocalStorage(
  isOn: boolean
) {
  localStorage.setItem(
    learningShowEnglishPronunciationSelectViewKey,
    isOn ? "on" : "off"
  );
}

export function getLearningIntervalUnitCountFromLocalStorage(): number {
  const isSectionBasedStudy = isOnSectionBasedStudyFromLocalStorage();
  if (!isSectionBasedStudy) {
    return 0;
  }

  const storedCount = localStorage.getItem(learningIntervalUnitCountKey);
  return storedCount ? JSON.parse(storedCount) : 20;
}

export function setLearningIntervalUnitCountToLocalStroage(count: number) {
  localStorage.setItem(learningIntervalUnitCountKey, JSON.stringify(count));
}

export function getLearningReviewLatestTypeFromLocalStroage(): LearningType {
  const storedType = localStorage.getItem(learningReviewLatestTypeKey);
  if (storedType === null || storedType === undefined) {
    return LearningType.spellingQuiz;
  } else {
    return JSON.parse(storedType);
  }
}

export function setLearningReviewLatestType(type: LearningType) {
  if (type === null || type === undefined) return;
  localStorage.setItem(learningReviewLatestTypeKey, JSON.stringify(type));
}

export function getLearningReviewLatestRangeFromLocalStorage(): LearningHistoryRange {
  const storedRange = localStorage.getItem(learningReviewLatestRangeKey);
  if (storedRange === null || storedRange === undefined) {
    return LearningHistoryRange.day;
  } else {
    return JSON.parse(storedRange);
  }
}

export function setLearningReviewLatestRangeToLocalStroage(
  range: LearningHistoryRange
) {
  if (range === null || range === undefined) return;
  localStorage.setItem(learningReviewLatestRangeKey, JSON.stringify(range));
}

export function getLearningReviewLatestAnswerTypeFromLocalStorage(): LearningAnswerType[] {
  const storedAnswerType = localStorage.getItem(
    learningReviewLatestAnswerTypeKey
  );
  if (storedAnswerType === null || storedAnswerType === undefined) {
    return [LearningAnswerType.correct, LearningAnswerType.incorrect];
  } else {
    return JSON.parse(storedAnswerType);
  }
}

export function setLearningReviewLatestAnswerTypeToLocalStroage(
  answerType: LearningAnswerType[]
) {
  if (
    answerType === null ||
    answerType === undefined ||
    answerType.length === 0
  )
    return;
  localStorage.setItem(
    learningReviewLatestAnswerTypeKey,
    JSON.stringify(answerType)
  );
}

export function getLearningSelectedGroupsFromLocalStorage(): Group[] {
  const storedGroups = localStorage.getItem(learningSelectedGroupsKey);
  if (storedGroups === null || storedGroups === undefined) {
    return [];
  } else {
    return JSON.parse(storedGroups);
  }
}

export function setLearningSelectedGroupsToLocalStroage(
  selectedGroups: Group[]
) {
  if (selectedGroups.length === 1 && selectedGroups[0].isAllGroup) {
    localStorage.setItem(learningSelectedGroupsKey, JSON.stringify([]));
  } else if (
    selectedGroups.length === 1 &&
    selectedGroups[0].isUnspecifiedGroup
  ) {
    const group = new Group(0, 0, "", 0, "", 0);
    group.isUnspecifiedGroup = true;
    localStorage.setItem(learningSelectedGroupsKey, JSON.stringify([group]));
  } else {
    localStorage.setItem(
      learningSelectedGroupsKey,
      JSON.stringify(selectedGroups)
    );
  }
}

export function getLearningSelectedLevelFromLocalStroage(): TermLevel[] {
  const storedLevels = localStorage.getItem(learningSelectedLevlesKey);
  if (storedLevels === null || storedLevels === undefined) {
    localStorage.setItem(
      learningSelectedLevlesKey,
      JSON.stringify([
        TermLevel.difficult,
        TermLevel.familiar,
        TermLevel.perfect,
      ])
    );
    return [TermLevel.difficult, TermLevel.familiar, TermLevel.perfect];
  } else {
    return JSON.parse(storedLevels);
  }
}

export function setLearningSelectedLevelsToLocalStroage(
  selectedLevels: TermLevel[]
) {
  localStorage.setItem(
    learningSelectedLevlesKey,
    JSON.stringify(selectedLevels)
  );
}

export function getLearningReviewGroupSortStyleFromLocalStorage(): LearningReviewSortStyle {
  if (localStorage.getItem(learningReviewGroupSortStyleKey) === "named") {
    return LearningReviewSortStyle.named;
  } else if (
    localStorage.getItem(learningReviewGroupSortStyleKey) === "mostLearned"
  ) {
    return LearningReviewSortStyle.termsCount;
  } else if (
    localStorage.getItem(learningReviewGroupSortStyleKey) === "leastLeanred"
  ) {
    return LearningReviewSortStyle.termsCountReverse;
  } else if (
    localStorage.getItem(learningReviewGroupSortStyleKey) === "incorrectCount"
  ) {
    return LearningReviewSortStyle.incorrectTermsCount;
  } else {
    localStorage.setItem(learningReviewGroupSortStyleKey, "named");
    return LearningReviewSortStyle.named;
  }
}

export function setLearningReviewGroupSortStyleToLocalStorage(
  style: LearningReviewSortStyle
) {
  switch (style) {
    case LearningReviewSortStyle.named:
      localStorage.setItem(learningReviewGroupSortStyleKey, "named");
      break;
    case LearningReviewSortStyle.termsCount:
      localStorage.setItem(learningReviewGroupSortStyleKey, "mostLearned");
      break;
    case LearningReviewSortStyle.termsCountReverse:
      localStorage.setItem(learningReviewGroupSortStyleKey, "leastLeanred");
      break;
    case LearningReviewSortStyle.incorrectTermsCount:
      localStorage.setItem(learningReviewGroupSortStyleKey, "incorrectCount");
      break;
  }
}

export function getLearningSortTypeFromLocalStorage(): SortType {
  if (localStorage.getItem(learningSortTypeKey) === "recent") {
    return SortType.recent;
  } else if (localStorage.getItem(learningSortTypeKey) === "old") {
    return SortType.old;
  } else if (localStorage.getItem(learningSortTypeKey) === "abc") {
    return SortType.abc;
  } else if (localStorage.getItem(learningSortTypeKey) === "random") {
    return SortType.random;
  } else {
    localStorage.setItem(learningSortTypeKey, "random");
    return SortType.random;
  }
}

export function setLearningSortTypeToLocalStorage(type: SortType) {
  switch (type) {
    case SortType.recent:
      localStorage.setItem(learningSortTypeKey, "recent");
      break;
    case SortType.old:
      localStorage.setItem(learningSortTypeKey, "old");
      break;
    case SortType.abc:
      localStorage.setItem(learningSortTypeKey, "abc");
      break;
    case SortType.random:
      localStorage.setItem(learningSortTypeKey, "random");
      break;
  }
}

// 학습별 퀴즈 방식 / 메모 표기
// 받아쓰기 퀴즈 방식은 원래부터 별도 키(spellingQuizQuestionType)를 사용
// 플래시카드, 객관식은 learningQuestionTypeKey, 모든 학습은 learningMemoPositionKey 를 공유했었음 (마이그레이션용으로 남겨둠)
export function getQuestionTypeFromLocalStorage(
  learningType: LearningType
): QuestionType {
  switch (learningType) {
    case LearningType.flashcards:
      return parseQuestionType(
        localStorage.getItem(flashcardsQuestionTypeKey) ??
          localStorage.getItem(learningQuestionTypeKey),
        QuestionType.termFirst
      );
    case LearningType.multiplechoice:
      return parseQuestionType(
        localStorage.getItem(multipleChoiceQuestionTypeKey) ??
          localStorage.getItem(learningQuestionTypeKey),
        QuestionType.termFirst
      );
    case LearningType.spellingQuiz:
      return parseQuestionType(
        localStorage.getItem(spellingQuizQuestionType),
        QuestionType.definitionFirst
      );
  }
}

export function setQuestionTypeToLocalStorage(
  learningType: LearningType,
  type: QuestionType
) {
  const value = questionTypeToStorageValue(type);
  if (value === null) return;
  localStorage.setItem(questionTypeKeyOf(learningType), value);
}

function questionTypeKeyOf(learningType: LearningType): string {
  switch (learningType) {
    case LearningType.flashcards:
      return flashcardsQuestionTypeKey;
    case LearningType.multiplechoice:
      return multipleChoiceQuestionTypeKey;
    case LearningType.spellingQuiz:
      return spellingQuizQuestionType;
  }
}

function parseQuestionType(
  value: string | null,
  defaultValue: QuestionType
): QuestionType {
  switch (value) {
    case "termFirst":
      return QuestionType.termFirst;
    case "defiFirst":
      return QuestionType.definitionFirst;
    case "pronFirst":
      return QuestionType.pronFirst;
    case "random":
      return QuestionType.random;
    default:
      return defaultValue;
  }
}

function questionTypeToStorageValue(type: QuestionType): string | null {
  switch (type) {
    case QuestionType.termFirst:
      return "termFirst";
    case QuestionType.definitionFirst:
      return "defiFirst";
    case QuestionType.pronFirst:
      return "pronFirst";
    case QuestionType.random:
      return "random";
    default:
      return null;
  }
}

export function getMemoPositionFromLocalStorage(
  learningType: LearningType
): MemoPosition {
  const defaultValue =
    learningType === LearningType.flashcards
      ? MemoPosition.onDefinition
      : MemoPosition.none;
  const value =
    localStorage.getItem(memoPositionKeyOf(learningType)) ??
    localStorage.getItem(learningMemoPositionKey);
  switch (value) {
    case "onTerm":
      return MemoPosition.onTerm;
    case "onDefi":
      return MemoPosition.onDefinition;
    case "none":
      return MemoPosition.none;
    default:
      return defaultValue;
  }
}

export function setMemoPositionToLocalStorage(
  learningType: LearningType,
  position: MemoPosition
) {
  const key = memoPositionKeyOf(learningType);
  switch (position) {
    case MemoPosition.onTerm:
      localStorage.setItem(key, "onTerm");
      break;
    case MemoPosition.onDefinition:
      localStorage.setItem(key, "onDefi");
      break;
    case MemoPosition.none:
      localStorage.setItem(key, "none");
      break;
  }
}

function memoPositionKeyOf(learningType: LearningType): string {
  switch (learningType) {
    case LearningType.flashcards:
      return flashcardsMemoPositionKey;
    case LearningType.multiplechoice:
      return multipleChoiceMemoPositionKey;
    case LearningType.spellingQuiz:
      return spellingQuizMemoPositionKey;
  }
}

export function getLearningPronunciationPositionFromLocalStroage(): PronunciationPosition {
  if (localStorage.getItem(learningPronunciationPrositionKey) === "onTerm") {
    return PronunciationPosition.onTerm;
  } else if (
    localStorage.getItem(learningPronunciationPrositionKey) === "onDefi"
  ) {
    return PronunciationPosition.onDefinition;
  } else {
    localStorage.setItem(learningPronunciationPrositionKey, "onTerm");
    return PronunciationPosition.onTerm;
  }
}

export function setLearningPronunciationPositionToLocalStorage(
  position: PronunciationPosition
) {
  switch (position) {
    case PronunciationPosition.onTerm:
      localStorage.setItem(learningPronunciationPrositionKey, "onTerm");
      break;
    case PronunciationPosition.onDefinition:
      localStorage.setItem(learningPronunciationPrositionKey, "onDefi");
      break;
  }
}

export function isOnSectionBasedStudyFromLocalStorage(): boolean {
  const value = localStorage.getItem(learningSectionBasedStudyKey);
  if (value === null) {
    setIsOnLearningSectionBasedStudyToLocalStorage(true);
    return true;
  }

  return value === "on";
}

export function setIsOnLearningSectionBasedStudyToLocalStorage(isOn: boolean) {
  const value = isOn ? "on" : "off";
  localStorage.setItem(learningSectionBasedStudyKey, value);
}

export function isOnLearningTextToSpeechFromLocalStorage(): boolean {
  const value = localStorage.getItem(learningTextToSpeechKey);
  if (value !== null) {
    if (value === "on") {
      return true;
    } else {
      return false;
    }
  } else {
    setIsOnLearningTextToSpeechToLocalStorage(true);
    return true;
  }
}

export function setIsOnLearningTextToSpeechToLocalStorage(isOn: boolean) {
  const value = isOn ? "on" : "off";
  localStorage.setItem(learningTextToSpeechKey, value);
}

// 오답노트는 객관식, 받아쓰기에만 있고 학습별로 따로 저장한다 (기본값은 앱과 동일하게 객관식 off, 받아쓰기 on)
export function isOnWrongAnswerNoteFromLocalStorage(
  learningType: LearningType
): boolean {
  switch (learningType) {
    case LearningType.multiplechoice:
      return localStorage.getItem(multipleChoiceWrongAnswerNoteKey) === "on";
    case LearningType.spellingQuiz:
      return localStorage.getItem(spellingQuizWrongAnswerNoteKey) !== "off";
    case LearningType.flashcards:
      return false;
  }
}

export function setIsOnWrongAnswerNoteToLocalStorage(
  learningType: LearningType,
  isOn: boolean
) {
  const value = isOn ? "on" : "off";
  switch (learningType) {
    case LearningType.multiplechoice:
      localStorage.setItem(multipleChoiceWrongAnswerNoteKey, value);
      break;
    case LearningType.spellingQuiz:
      localStorage.setItem(spellingQuizWrongAnswerNoteKey, value);
      break;
    case LearningType.flashcards:
      break;
  }
}
